import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AIUserStats {
  user_id: string;
  total_requests: number;
  total_tokens: number;
  last_request: string;
  user_name?: string;
  user_email?: string;
}

interface AIUsageStats {
  topUsers: AIUserStats[];
  totalRequests: number;
  totalTokens: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAIUsageStats(): AIUsageStats {
  const [topUsers, setTopUsers] = useState<AIUserStats[]>([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get top users using the database function
      const { data: topUsersData, error: topUsersError } = await supabase
        .rpc('get_top_ai_users', { limit_count: 20 });
      
      if (topUsersError) throw topUsersError;

      // Get user names from profiles
      const userIds = (topUsersData || []).map((u: { user_id: string }) => u.user_id);
      
      let profilesMap: Record<string, { name: string | null }> = {};
      if (userIds.length > 0) {
        // Use a service role query or just display user IDs for now
        // Since we're admin, we can query profiles directly via the admin role
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', userIds);
        
        if (profiles) {
          profilesMap = profiles.reduce((acc, p) => {
            acc[p.id] = { name: p.name };
            return acc;
          }, {} as Record<string, { name: string | null }>);
        }
      }

      const enrichedUsers: AIUserStats[] = (topUsersData || []).map((u: {
        user_id: string;
        total_requests: number;
        total_tokens: number;
        last_request: string;
      }) => ({
        user_id: u.user_id,
        total_requests: Number(u.total_requests),
        total_tokens: Number(u.total_tokens),
        last_request: u.last_request,
        user_name: profilesMap[u.user_id]?.name || undefined,
      }));

      setTopUsers(enrichedUsers);
      
      // Calculate totals
      const totals = enrichedUsers.reduce(
        (acc, u) => ({
          requests: acc.requests + u.total_requests,
          tokens: acc.tokens + u.total_tokens,
        }),
        { requests: 0, tokens: 0 }
      );
      
      setTotalRequests(totals.requests);
      setTotalTokens(totals.tokens);
    } catch (err) {
      console.error('Failed to fetch AI usage stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch AI usage stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    topUsers,
    totalRequests,
    totalTokens,
    loading,
    error,
    refetch: fetchStats,
  };
}
